import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  // Debounce handle for hover/focus-triggered prefetch, so scanning
  // across a row of cards doesn't fire one fetch per card.
  #prefetchTimeout = null;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  #fetchProductPageHandler = () => {
    clearTimeout(this.#prefetchTimeout);
    this.#prefetchTimeout = setTimeout(() => {
      if (!this.refs.quickAdd?.cachedProductHtml) {
        this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
      }
    }, 150);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Intercept swatch clicks before slideshow animation starts (capture phase)
    this.addEventListener('click', this.#handleSwatchClickImmediate, true);

    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('click', this.#handleSwatchClickImmediate, true);
    // Was missing before: every card that connected leaked one listener on
    // the shared mediaQueryLarge MediaQueryList forever, which compounds
    // badly with infinite-scroll grids that create hundreds of cards.
    mediaQueryLarge.removeEventListener('change', this.#handleQuickAdd);
    clearTimeout(this.#prefetchTimeout);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  #handleSwatchClickImmediate = (event) => {
    // Skip if click came from inside a related-color picker (has buttons, not labels)
    if (event.target instanceof Element && event.target.closest('[data-action="select-color"]')) return;

    const label = event.target instanceof Element && event.target.closest('label[data-media-id]');
    if (!label) return;

    const mediaId = label.getAttribute('data-media-id');
    if (!mediaId) return;

    const { slideshow } = this.refs;
    if (!slideshow) return;

    const { slides = [] } = slideshow.refs;

    for (const slide of slides) {
      if (slide.getAttribute('variant-image') == null) continue;
      slide.hidden = slide.getAttribute('slide-id') !== mediaId;
    }

    slideshow.select({ id: mediaId }, undefined, { animate: false });
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);

    this.#updateQuickAddVariantId(event);

    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;
  };

  #updateQuickAddVariantId(event) {
    const newHtml = event.detail.data.html;
    if (!newHtml) return;

    const newVariantId = newHtml.querySelector('[ref="variantId"]')?.value;
    if (!newVariantId) return;

    const variantIdInput = this.querySelector('[ref="variantId"]');
    if (variantIdInput instanceof HTMLInputElement) {
      variantIdInput.value = newVariantId;
    }
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      this.refs.productCardLink.href = anchorElement.href;
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;
        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (typeof slideshow.select !== 'function') {
      console.warn('Slideshow select method not available');
      return;
    }

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');

    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    } else if (!this.variantPicker?.selectedOption) {
      if (typeof slideshow.previous === 'function') {
        slideshow.previous(undefined, { animate: false });
      }
      return;
    }

    const id = this.variantPicker.selectedOption.dataset.optionMediaId;
    if (!id) {
      if (typeof slideshow.previous === 'function') {
        slideshow.previous(undefined, { animate: false });
      }
      return;
    }

    slideshow.select({ id }, undefined, { animate: false });
  };
  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, a, [tabindex="1"]');

    if (interactiveElement && interactiveElement !== this.refs.productCardLink) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      history.replaceState({}, '', url.toString());
    }

    this.#navigateToURL(event, linkURL);
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {HTMLButtonElement | null} */
  #previousActiveButton = null;

  /** @type {Map<string, Element>} */
  #cardCache = new Map();

  // Debounce handle so sweeping across swatches doesn't fire a
  // prefetch per swatch.
  #prefetchTimeout = null;

  // Cap on cached remote cards. Prevents unbounded memory growth
  // on long infinite-scroll sessions with lots of swatch interaction.
  #CACHE_LIMIT = 30;

  connectedCallback() {
    super.connectedCallback?.();

    if (this.dataset.relatedColorSwatches === 'true') {
      this.addEventListener('click', this.#handleSwatchClick);
      this.addEventListener('mouseenter', this.#handlePrefetch, true);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.removeEventListener('click', this.#handleSwatchClick);
    this.removeEventListener('mouseenter', this.#handlePrefetch, true);

    clearTimeout(this.#prefetchTimeout);
    this.#fetchController?.abort();
  }

  showAllSwatches(event) {
    event?.preventDefault();
    const { overflowList } = this.refs;
    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }

  /** @returns {HTMLElement | null} */
  get #productCard() {
    return this.closest('product-card');
  }

  /** PREFETCH on hover — debounced so a quick sweep across swatches
   *  doesn't fire a request per swatch. */
  #handlePrefetch = (event) => {
    const button = event.target.closest('[data-action="select-color"]');
    if (!(button instanceof HTMLButtonElement)) return;

    const productUrl = button.dataset.productUrl;
    if (!productUrl) return;

    if (this.#cardCache.has(productUrl)) return;

    clearTimeout(this.#prefetchTimeout);
    this.#prefetchTimeout = setTimeout(() => {
      this.#fetchProductCard(productUrl).catch(() => {});
    }, 150);
  };

  /** CLICK handler */
  #handleSwatchClick = async (event) => {
    const button = event.target.closest('[data-action="select-color"]');
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.disabled) return;

    const productUrl = button.dataset.productUrl;
    if (!productUrl) return;

    if (button.getAttribute('aria-pressed') === 'true') return;

    this.#setActiveButton(button);
    button.setAttribute('data-loading', 'true');

    try {
      const remoteCard = await this.#fetchProductCard(productUrl);

      if (!remoteCard) return;

      this.#morphCard(remoteCard);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;

      console.error('[related-color-swatches] fetch failed', err);
      this.#restoreActiveState();
    } finally {
      button.removeAttribute('data-loading');
    }
  };

  /** FETCH + CACHE + SAFE ABORT */
  async #fetchProductCard(productUrl) {
    if (this.#cardCache.has(productUrl)) {
      return this.#cardCache.get(productUrl);
    }

    this.#fetchController?.abort();

    const controller = new AbortController();
    this.#fetchController = controller;

    const card = this.#productCard;

    const sectionId =
      card?.closest('[data-section-id]')?.dataset.sectionId ??
      card?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');

    const url = new URL(productUrl, window.location.origin);
    if (sectionId) url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), {
      signal: controller.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (controller !== this.#fetchController) return null;

    if (!response.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const remoteCard = doc.querySelector('product-card');

    if (remoteCard) {
      // Evict oldest entry if we're at the cap (Map preserves insertion order)
      if (this.#cardCache.size >= this.#CACHE_LIMIT) {
        const oldestKey = this.#cardCache.keys().next().value;
        this.#cardCache.delete(oldestKey);
      }
      this.#cardCache.set(productUrl, remoteCard);
    }

    return remoteCard;
  }

  /** MORPH UI */
  #morphCard(remoteCard) {
    const card = this.#productCard;
    if (!card) return;

    morph(card, remoteCard);

    const cardLink = card.closest('product-card-link');
    if (cardLink) {
      const newFeaturedMediaUrl = remoteCard.closest('product-card-link')?.getAttribute('data-featured-media-url');

      if (newFeaturedMediaUrl) {
        cardLink.setAttribute('data-featured-media-url', newFeaturedMediaUrl);
      }

      const newProductId = remoteCard.dataset.productId;
      if (newProductId) {
        cardLink.setAttribute('data-product-id', newProductId);
      }
    }

    const slideshow = card.querySelector('slideshow-component');
    if (slideshow && typeof slideshow.select === 'function') {
      const initialSlide = slideshow.initialSlide;
      const initialSlideId = initialSlide?.getAttribute('slide-id');

      if (initialSlideId) {
        slideshow.select({ id: initialSlideId }, undefined, { animate: false });
      } else if (slideshow.slides?.length) {
        slideshow.select(0, undefined, { animate: false });
      }
    }

    card.dispatchEvent(
      new CustomEvent('related-color:changed', {
        bubbles: true,
        detail: { productId: remoteCard.dataset.productId },
      })
    );
  }

  /** ACTIVE STATE */
  #setActiveButton(button) {
    this.#previousActiveButton = this.querySelector('[data-action="select-color"][aria-pressed="true"]');

    for (const btn of this.querySelectorAll('[data-action="select-color"]')) {
      const isTarget = btn === button;

      btn.setAttribute('aria-pressed', isTarget ? 'true' : 'false');
      btn.classList.toggle('related-color-swatches__swatch--active', isTarget);
      btn.toggleAttribute('aria-current', isTarget);

      /** @type {HTMLButtonElement} */
      btn.disabled = isTarget;
    }
  }

  /** RESTORE on error */
  #restoreActiveState() {
    if (this.#previousActiveButton instanceof HTMLButtonElement) {
      this.#setActiveButton(this.#previousActiveButton);
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}